import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useAttributesStateStore } from '@/components/datamodel/hooks/useAttributesStore';
import { useValidationsStore } from '@/components/datamodel/hooks/useValidationsStore';
import { PersistedItem } from '@/types/DataModel';

import { useItemsListChangesQueues } from './useItemsListChangesQueues';

interface EditingRowContextValue {
    /**
     * Primary key of the row currently in edit mode, or `null` when no
     * row is being edited. Mirrors `$scope.rowEditItem.primaryKey__` from
     * the legacy items-controller.
     */
    editingRowId: string | null;
    /**
     * Attribute name of the single cell in edit mode. LAX-10059: a
     * double-click edits that cell only, not the whole row.
     */
    editingAttributeName: string | null;
    /**
     * Set when the user single-clicks an upload icon so the editor opens
     * the asset dialog immediately (one click, not double-click then click).
     */
    pendingUploadAttribute: string | null;
    /**
     * Value written on the same gesture that enters edit mode (a single
     * click on a checkbox). The row form applies it once, then clears it.
     */
    initialPatch: Partial<PersistedItem> | null;
    /**
     * A mutable ref that stays `true` while the user is actively editing
     * a cell in the current row. Mirrors legacy `rowEditingActive`. Exposed
     * as a ref so cell components can toggle it without triggering renders.
     */
    isEditingActiveRef: React.MutableRefObject<boolean>;
    /**
     * Enter edit mode on `item`. If a different row was already being
     * edited, its queues are stopped (matching legacy
     * `laxGridStartCellEdit`). Sets `isEditingActive = true`.
     */
    beginEdit: (
        item: PersistedItem,
        attributeName: string,
        options?: { openUpload?: boolean; initialPatch?: Partial<PersistedItem> },
    ) => void;
    /** Drops `initialPatch` after the row form has applied it. */
    clearInitialPatch: () => void;
    /** Drops the one-shot upload-dialog request after the editor consumes it. */
    clearPendingUpload: () => void;
    /**
     * Single click on another editable cell waits briefly so a double-click
     * can switch fields instead of leaving edit mode.
     */
    scheduleLeave: () => void;
    cancelScheduledLeave: () => void;
    /**
     * Leave the active cell edit (blur). Clears `isEditingActive` and
     * force-flushes the store queue so the save fires immediately, exactly
     * like legacy `laxGridStopCellEdit`.
     */
    endEdit: () => void;
    /**
     * Called before opening a modal (item editor / delete confirm / item
     * review): flushes the store queue and stops all three queues so no
     * in-flight change lingers while the modal is open. Mirrors the
     * `stopItemChangesQueues()` + `stopGridCellEditListeners()` pattern
     * scattered through items-controller.js (923-1425).
     */
    pauseEditing: () => void;
    /**
     * Commit the current row edit and exit edit mode entirely: flush the
     * store queue so pending typing is persisted, then stop all queues
     * and clear the editing row state so the row swaps back to read-only
     * cells. Bound to `Enter` and to click-outside-the-row by
     * `EditableRowFormProvider`.
     */
    commitEdit: () => void;
    /**
     * Abort the current row edit: stop all three queues *without* flushing
     * the store queue so no pending change is persisted, exit edit mode,
     * and clear the shared validation / attribute stores. Bound to `Esc`
     * by `EditableRowFormProvider`.
     */
    discardEdit: () => void;
    /**
     * Fan a patch out to all three queues. Called by
     * `EditableRowFormProvider` on every RHF `watch` change.
     */
    enqueue: (patch: Partial<PersistedItem>) => void;
    /** Ref to the row snapshot the queues are pointed at, used as the RHF form seed. */
    editingItemRef: React.MutableRefObject<PersistedItem | null>;
}

const EditingRowContext = createContext<EditingRowContextValue | undefined>(
    undefined,
);

type QueueCallbacks = Parameters<typeof useItemsListChangesQueues>[0];
type EditingRowProviderProps = PropsWithChildren<
    Partial<Omit<QueueCallbacks, 'isEditingActiveRef'>>
>;

const EditingRowProvider = ({
    children,
    onValidateSuccess,
    onValidateError,
    onSaveSuccess,
    onSaveError,
}: EditingRowProviderProps) => {
    const [editingRowId, setEditingRowId] = useState<string | null>(null);
    const [editingAttributeName, setEditingAttributeName] = useState<string | null>(null);
    const [pendingUploadAttribute, setPendingUploadAttribute] = useState<string | null>(null);
    const [initialPatch, setInitialPatch] = useState<Partial<PersistedItem> | null>(null);
    const isEditingActiveRef = useRef<boolean>(false);
    const editingItemRef = useRef<PersistedItem | null>(null);
    const leaveTimerRef = useRef<number | null>(null);
    const commitEditRef = useRef<(() => void) | null>(null);

    const cancelScheduledLeave = useCallback(() => {
        if (leaveTimerRef.current != null) {
            window.clearTimeout(leaveTimerRef.current);
            leaveTimerRef.current = null;
        }
    }, []);

    const clearEditingState = useCallback(() => {
        editingItemRef.current = null;
        setEditingRowId(null);
        setEditingAttributeName(null);
        setPendingUploadAttribute(null);
        setInitialPatch(null);
    }, []);

    const { startForRow, stopAll, enqueue, flushStore } = useItemsListChangesQueues({
        isEditingActiveRef,
        onValidateSuccess,
        onValidateError,
        onSaveSuccess,
        onSaveError,
    });

    const beginEdit = useCallback(
        (
            item: PersistedItem,
            attributeName: string,
            options?: { openUpload?: boolean; initialPatch?: Partial<PersistedItem> },
        ) => {
            cancelScheduledLeave();
            if (!item.primaryKey__) {
                return;
            }
            setEditingAttributeName(attributeName);
            setPendingUploadAttribute(options?.openUpload ? attributeName : null);
            setInitialPatch(options?.initialPatch ?? null);
            // Legacy laxGridStartCellEdit: switch queues to the new row
            // only when the row actually changes, otherwise just flag
            // the row as actively editing. LAX-10059 keeps the form mounted
            // when the user double-clicks a different cell in the same row.
            if (editingItemRef.current?.primaryKey__ !== item.primaryKey__) {
                editingItemRef.current = item;
                setEditingRowId(item.primaryKey__);
                startForRow(item);

                // Seed attribute state from the row's server-computed
                // attributeStates__ so cells reflect readonly / hidden
                // from the very first render (matches useValidations
                // applySavedAttributeStates in the item editor).
                const store = useAttributesStateStore.getState();
                store.clearState();
                if (item.attributeStates__?.length) {
                    store.initializeWithStates(item.attributeStates__);
                }
            }
            isEditingActiveRef.current = true;
        },
        [cancelScheduledLeave, startForRow],
    );

    const endEdit = useCallback(() => {
        if (!editingItemRef.current) return;
        // Order matters: clear the "active edit" flag BEFORE flushing so
        // shouldSkip() returns false and the store queue actually runs
        // (mirrors items-controller.js `laxGridStopCellEdit`).
        isEditingActiveRef.current = false;
        flushStore();
    }, [flushStore]);

    const pauseEditing = useCallback(() => {
        cancelScheduledLeave();
        if (!editingItemRef.current) return;
        isEditingActiveRef.current = false;
        // Give the store queue a chance to persist any pending patch
        // before we tear things down (best-effort - matches the effective
        // legacy behaviour: blur usually fires before a modal opens).
        flushStore();
        stopAll();
        clearEditingState();
    }, [cancelScheduledLeave, clearEditingState, flushStore, stopAll]);

    const commitEdit = useCallback(() => {
        cancelScheduledLeave();
        if (!editingItemRef.current) return;
        // Same teardown as `pauseEditing` (flush pending patch, stop the
        // queues, clear the editing row state) but expressed as its own
        // callback so the intent at the call site - the user finished
        // editing by pressing Enter or clicking outside - stays obvious.
        isEditingActiveRef.current = false;
        flushStore();
        stopAll();
        clearEditingState();
    }, [cancelScheduledLeave, clearEditingState, flushStore, stopAll]);

    const scheduleLeave = useCallback(() => {
        cancelScheduledLeave();
        leaveTimerRef.current = window.setTimeout(() => {
            leaveTimerRef.current = null;
            commitEditRef.current?.();
        }, 500);
    }, [cancelScheduledLeave]);

    const discardEdit = useCallback(() => {
        cancelScheduledLeave();
        if (!editingItemRef.current) return;
        // Order matters: mark editing as inactive first so any late queue
        // check treats us as idle, then stop the queues *without* calling
        // `flushStore()` so pending typing does not turn into a POST /items.
        isEditingActiveRef.current = false;
        stopAll();

        // Wipe the shared validation store for this row. The datamodel
        // components subscribe to it directly, so leaving stale entries
        // would flag the freshly-reverted cells red on the next render.
        const validations = useValidationsStore.getState();
        validations.clearErrors();
        validations.clearWarnings();
        validations.clearLocalValidationErrors();
        useAttributesStateStore.getState().clearState();

        clearEditingState();
    }, [cancelScheduledLeave, clearEditingState, stopAll]);

    const clearPendingUpload = useCallback(() => {
        setPendingUploadAttribute(null);
    }, []);

    const clearInitialPatch = useCallback(() => {
        setInitialPatch(null);
    }, []);

    useEffect(() => {
        commitEditRef.current = commitEdit;
    }, [commitEdit]);

    const value = useMemo<EditingRowContextValue>(
        () => ({
            editingRowId,
            editingAttributeName,
            pendingUploadAttribute,
            initialPatch,
            isEditingActiveRef,
            beginEdit,
            clearPendingUpload,
            clearInitialPatch,
            scheduleLeave,
            cancelScheduledLeave,
            endEdit,
            pauseEditing,
            commitEdit,
            discardEdit,
            enqueue,
            editingItemRef,
        }),
        [
            editingRowId,
            editingAttributeName,
            pendingUploadAttribute,
            initialPatch,
            beginEdit,
            clearPendingUpload,
            clearInitialPatch,
            scheduleLeave,
            cancelScheduledLeave,
            endEdit,
            pauseEditing,
            commitEdit,
            discardEdit,
            enqueue,
        ],
    );

    return (
        <EditingRowContext.Provider value={value}>
            {children}
        </EditingRowContext.Provider>
    );
};

const useEditingRow = (): EditingRowContextValue | undefined =>
    useContext(EditingRowContext);

/**
 * Same as `useEditingRow` but throws when called outside of an
 * `EditingRowProvider`. Prefer this in components that always live under
 * the provider (e.g. `EditableItemCell`).
 */
const useRequiredEditingRow = (): EditingRowContextValue => {
    const value = useContext(EditingRowContext);
    if (!value) {
        throw new Error(
            'useRequiredEditingRow: EditingRowProvider is missing in the tree',
        );
    }
    return value;
};

export {
    EditingRowContext,
    EditingRowProvider,
    useEditingRow,
    useRequiredEditingRow,
};
export type { EditingRowContextValue };
